/* eslint-disable no-console */
/* eslint-env amd */

import { throwIfApiError } from '@atlas/api-utils/src/apiUtils';

/**
 *
 * @param err
 */
function defaultRejectedPromiseHandler(err) {
    console.error('RequestQueue promise error: ', err);
}

/**
 * We require a results array which has a response for each request that was
 * sent to the Atlas server. Sometimes the Atlas server doesn't provide us
 * with responses for requests, and sometimes it "hides" the responses as
 * properties of other JSON objects that it arbitrarily creates. Try to
 * unpick this mess and at least provide one response object for each
 * request (even if the response is "I haven't a clue what the result it")
 *
 * @param {Array} results - the JSON objects returned from the Atlas server as API_RESULTS
 * @param {Array} requests - the original JSON objects that were sent in the request
 * @returns {Array} adjusted "results" array with (at least) one result object for each
 * request object.
 */
function decodeServerResponse(results, requests) {
    // Rather than return a "nothing" response, we return something that will be treated
    // as a real error.
    const missingResponse = { API_Message: 'Missing response from Atlas server', API_Error: -1 };

    // Check for a bad response back from Atlas server...
    // Generate an empty response for each request.
    if (!(results instanceof Array)) {
        results = new Array(requests.length).fill(missingResponse);
    }

    // Transform any null responses into empty-object responses
    const nullResponse = {};
    results = results.map((x) => (x === null ? nullResponse : x));

    // If there are insufficient responses back from the Atlas server,
    // generate some bogus empty replies to use. However we have no way
    // of telling which requests should get these bogus responses.
    if (results.length < requests.length) {
        results = results.concat(new Array(requests.length - results.length).fill(missingResponse));
    }

    return results;
}

/**
 * A queue of Atlas requests and associated promise functions. Each entry is
 * of the form:
 *
 *  { request: Object, promise: {resolve: <function>, reject: <function>} }
 *
 * Now includes a very nasty hack to ensure that the queue is a singleton object
 * even if multiple copies of this script are generated by webpack.
 */
window.apiQueue = window.apiQueue || [];
const apiQueue = window.apiQueue;

window.apiQueueAutoFlush = true;

/**
 *
 */
function stackTrace() {
    var err = new Error();
    let stack = err.stack;
    // Remove the first line that shows as "Error .."
    return stack.split('\n').slice(1).join('\n');
}

export class RequestQueue {
    constructor(sendRequest, rejectedPromiseHandler = defaultRejectedPromiseHandler) {
        this.sendRequest = sendRequest;
        this.timer = null;
        this.rejectedPromiseHandler = rejectedPromiseHandler;
    }

    set autoFlush(enable) {
        window.apiQueueAutoFlush = enable;
    }

    get autoFlush() {
        return window.apiQueueAutoFlush;
    }

    /**
     * @param {object} request - an object representing a request (or array of requests) to be sent to the Atlas server.
     * @param {object} options -
     * @param options.autoFlushRequest: true: flush requests using a timeout and initiates a timeout.
     *                                    false: doesn't initiate a timeout and depends on next request timeout to flush requests.
     * @param options.requestSentCb:  callback function to notify callers when request is sent to Altas.
     * @returns {Promise} a promise for the response from the Atlas server for this request
     */
    queueRequest(request, options = { autoFlushRequest: true }) {
        // Handle existing code that passes in autoFlushRequest as a boolean second parameter.
        if (typeof options === 'boolean') {
            console.warn('Request queue is using option which is deprecated, upgrading dependent packages may resolve this');
            const autoFlushRequest = options;
            options = { autoFlushRequest: autoFlushRequest };
        }

        // If the request is an array of objects then queue up each object
        // individually (each one getting their own promise). Then return a
        // single promise that each of these others are resolved. The returned
        // promise will resolve to a tuple (or array?) of values of these
        // promises.
        //
        // Unfortunately if there is an error, or errors, the combined promise
        // will only reflect the first error, not all of them. We probably should
        // arrange it so that if the combined promise fails, then the catch handler
        // is given an array of objects reflecting the combination of results &/or
        // error for each individual request in this array. This means we need a
        // catch handler of our own to get this information. Can we use
        // Promise.allSettled() for this?
        // Alternatively should this object be given some callback to use to select
        // which of the errors was most important to return?
        // The problem which caused this train of thought was a simple two object
        // request: get the work part, call a method which failed. In the original
        // response from the Atlas worker, we get a result plus an error back.
        // The first result is replaced with a bogus error saying that the operation
        // worked but was rolled back. This code then sees two errors, where the
        // first one is pretty meaningless but just indicates that the request
        // wasn't acted on (and so failed). The second error is important as it
        // says what was going wrong. However by queuing both requests together
        // these two responses are processed by this Promise.all step, and it is
        // the first (meaningless) error that gets reported, not the second one.
        // In this case the caller would prefer to get the error for the last
        // request - but it wouldn't always be the last one that was the important
        // error - so we need some way to decide which error out of the potentially
        // many that are produced from this array of requests - should be the one
        // that we report back as the failure of this returned promise.
        //
        // Q: Can we deal with this by revoking the promise for the real error
        //    before we revoke the promises for the pseudo errors? Would that
        //    result in the desired failed-promise being selected by the
        //    Promise.all call?
        if (request instanceof Array) {
            const promise = Promise.all(request.map((x) => this.queueRequest(x, options)));
            promise.catch(this.rejectedPromiseHandler);
            return promise;
        }

        // If the queue is currently empty, set up a timeout
        // callback to send the queued requests to the Atlas server.
        // Only set the timer if auto flush is not enabled.
        if (this.autoFlush && options.autoFlushRequest && this.timer === null) {
            this.timer = setTimeout(() => this.flushQueue(), 0);
        }

        // Add the request object to the queue of pending requests and return a promise for
        // the value that will be returned when the queue is processed.
        const promise = new Promise(function (resolve, reject) {
            apiQueue.push({
                request: request,
                promise: {
                    resolve: resolve,
                    reject: reject,
                    requestTrace: stackTrace()
                },
                requestSentCb: options.requestSentCb
            });
        });

        promise.catch(this.rejectedPromiseHandler);
        return promise;
    }

    /**
     * @description timeout function to dispatch all of the queued up atlas requests.
     * @param {string?} apiDesc - description for the set of requests.
     * @param {object?} apiId - Object that consists of ApiId and ApiSubId.
     */
    flushQueue(apiDesc = '', apiId = {}) {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }

        const queue = apiQueue.splice(0, apiQueue.length);

        const requests = queue.map((x) => x.request);
        const promises = queue.map((x) => x.promise);

        // notify through callaback that request is sent to Atlas.
        queue.forEach((x) => {
            if (x.requestSentCb !== undefined) {
                x.requestSentCb();
            }
        });

        //console.info('Sending request to Atlas server');
        // Convert the given objects (which may be simple JS objects or may be Atlas SDK objects)
        // into simple JS objects. These have a simpler representation in the browser's debugger
        // which makes it easier to see what the Atlas server is actually getting as input.
        //console.debug(JSON.parse(JSON.stringify(requests)));

        this.sendRequest(requests, apiDesc, apiId).then(this.resolvePromises(requests, promises)).catch(this.rejectPromises(requests, promises));
    }

    /**
     * @param {[JSON]} requests - array of Atlas API requests that were sent to the server
     * @param {[object]} promises - the resolve/reject functions of the promises that were given for each request
     * @returns {Function} - a function to be called to resolve all of the promises with the objects returned from an Atlas call
     */

    resolvePromises(requests, promises) {
        return function (results) {
            results = decodeServerResponse(results, requests);

            for (const index in promises) {
                const requestObj = requests[index];
                const resultObj = results[index];
                const promise = promises[index];

                try {
                    throwIfApiError(resultObj);
                    promise.resolve(resultObj, requestObj);
                } catch (err) {
                    err.stack = err.stack + '\n(queued Atlas request)\n' + promise.requestTrace;
                    promise.reject(err);
                }
            }
        };
    }

    /**
     * @param {[JSON]} requests - array of Atlas API requests that were sent to the server
     * @param {[object]} promises - the resolve/reject functions of the promises that were given for each request
     * @returns {Function} - a function to be called to reject all of the promises
     */

    rejectPromises(requests, promises) {
        return function (error) {
            if (!error) {
                error = new Error('No response from Atlas server');
            }
            for (const index in promises) {
                const requestObj = requests[index];
                const promise = promises[index];
                // Do we need to protect the promise.reject with a try? If the
                // rejection code throws an exception, won't the promise catch
                // this anyway?
                try {
                    console.log('Atlas request trace: ', promise.requestTrace);
                    promise.reject(error, requestObj);
                } catch (err) {
                    console.error(err);
                }
            }
        };
    }
}
